import './style.scss'
import React from 'react'
import { Box, Flex, Text, tokens } from 'src/componentv2/Base/ui'
import { ActionMenu } from 'src/componentv2/Base/ActionMenu'
import { fontMedium } from 'src/model/v2/interface/constants'
import { getCategoryStyle } from './categories'

export interface FAQCardProps {
    category: string
    question: string
    answer: string
    isExpanded?: boolean
    onToggleExpand?: () => void
    onEdit: () => void
    onDelete: () => void
    draggable?: boolean
    isDragging?: boolean
    isDragOver?: boolean
    onDragStart?: (e: React.DragEvent) => void
    onDragEnd?: (e: React.DragEvent) => void
    onDragOver?: (e: React.DragEvent) => void
    onDrop?: (e: React.DragEvent) => void
}

export const FAQCard: React.FC<FAQCardProps> = ({
                                                    category, question, answer, isExpanded = false, onToggleExpand,
                                                    onEdit, onDelete,
                                                    draggable = false, isDragging = false, isDragOver = false,
                                                    onDragStart, onDragEnd, onDragOver, onDrop,
                                                }) => {
    const categoryStyle = getCategoryStyle(category)

    return (
        <Box
            className={[
                'faq-card',
                isDragging ? 'faq-card--dragging' : '',
                isDragOver ? 'faq-card--drag-over' : '',
            ].join(' ').trim()}
            bgColor={tokens.white}
            borderColor="#D0D5DD"
            radius="1.2"
            padding="2"
            width="100%"
            draggable={draggable}
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDragOver={onDragOver}
            onDrop={onDrop}
        >
            <Flex justify="space-between" items="flex-start" gap="1.2">
                <Flex
                    gap="1.2"
                    items="flex-start"
                    style={{ flex: 1, minWidth: 0, cursor: onToggleExpand ? 'pointer' : 'default' }}
                    onClick={onToggleExpand}
                >
                    {draggable && (
                        <span className="faq-card__handle" aria-label="Drag to reorder">
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <circle cx="5" cy="4" r="1.2" fill="currentColor" />
                                <circle cx="11" cy="4" r="1.2" fill="currentColor" />
                                <circle cx="5" cy="8" r="1.2" fill="currentColor" />
                                <circle cx="11" cy="8" r="1.2" fill="currentColor" />
                                <circle cx="5" cy="12" r="1.2" fill="currentColor" />
                                <circle cx="11" cy="12" r="1.2" fill="currentColor" />
                            </svg>
                        </span>
                    )}

                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <span
                            className="faq-card__badge"
                            style={{ background: categoryStyle.bg, color: categoryStyle.color }}
                        >
                            {category}
                        </span>
                        <Text topMargin="0.6" fontSize="1.6" style={fontMedium} color={tokens.textDark}>
                            {question}
                        </Text>
                    </Box>
                </Flex>

                <Flex gap="0.4" items="center">
                    <ActionMenu
                        items={[
                            { label: 'Edit', onClick: onEdit },
                            { label: 'Delete', onClick: onDelete, variant: 'danger' },
                        ]}
                    />
                    {onToggleExpand && (
                        <button
                            type="button"
                            className="faq-card__chevron"
                            aria-label={isExpanded ? 'Collapse answer' : 'Expand answer'}
                            onClick={onToggleExpand}
                        >
                            <svg
                                width="14" height="14" viewBox="0 0 16 16" fill="none"
                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s ease' }}
                            >
                                <path d="M4 6L8 10L12 6" stroke={tokens.textMuted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                </Flex>
            </Flex>

            <Text
                topMargin="0.8"
                fontSize="1.4"
                color={tokens.textMuted}
                className={isExpanded ? 'faq-card__answer faq-card__answer--expanded' : 'faq-card__answer'}
                style={{ fontFamily: 'Gilmer Regular', fontWeight: 300 }}
            >
                {answer}
            </Text>
        </Box>
    )
}

export default FAQCard